// modules/crm/customers/CustomerFilter.jsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import Select from "react-select";
import { motion, AnimatePresence } from "framer-motion";
import { useDebounce } from "use-debounce";
import {
  Search,
  RotateCw,
  SlidersHorizontal,
  Plus,
  MoreVertical,
  BookOpen,
  UploadCloud,
  Tags,
  Eraser,
} from "lucide-react";

import customSelectStyles from "../../../components/ui/customSelectStyles";
import MobileAdvancedFilterSheet from "../../../components/ui/MobileAdvancedFilterSheet";
import { hasPermission } from "../../../utils/permissionUtils";

const TEXT = {
  searchPlaceholder: "Tìm tên, SĐT, địa chỉ…",
  refresh: "Làm mới",
  advanced: "Lọc nâng cao",
  advancedTitle: "Bộ lọc nâng cao",
  branch: "Chi nhánh",
  ward: "Phường / Xã",
  status: "Trạng thái chăm sóc",
  recordStatus: "Tình trạng hoạt động",
  owner: "Sales phụ trách",
  leadSource: "Nguồn khách",
  customerType: "Loại khách",
  schoolLevel: "Cấp học",
  tag: "Tags",
  allBranch: "Tất cả chi nhánh",
  allWard: "Tất cả phường / xã",
  allStatus: "Tất cả trạng thái",
  allRecordStatus: "Tất cả tình trạng",
  allOwner: "Tất cả sales",
  allLeadSource: "Tất cả nguồn",
  allCustomerType: "Tất cả loại",
  allSchoolLevel: "Tất cả cấp học",
  allTag: "Tất cả tags",
  clear: "Xóa lọc",
  apply: "ÁP DỤNG",
  addCustomer: "Thêm khách hàng",
  importExcel: "Nhập excel",
  moreOptions: "Tùy chọn khác",
  manageTags: "QUẢN LÝ TAGS",
  guideHtml: "HƯỚNG DẪN SỬ DỤNG",
  guideLoadFailed: "Không thể tải hướng dẫn!",
};

function readUserBranchId() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return (
      u?.branch?.id ||
      u?.branch_id ||
      u?.branchId ||
      u?.owner_branch_id ||
      u?.branch?.branch_id ||
      ""
    );
  } catch {
    return "";
  }
}

/** Mặc định: chi nhánh user đang đăng nhập + Đang hoạt động */
export function getDefaultCustomerFilters() {
  const branchId = readUserBranchId();
  return {
    keyword: "",
    branch_id: branchId ? String(branchId) : "",
    ward: "",
    status: "",
    record_status: "active",
    owner_id: "",
    lead_source: "",
    customer_type: "",
    school_level: "",
    tag_id: "",
  };
}

const statusLabelMap = {
  new: "Mới",
  active: "Đang chăm sóc",
  idle: "Không tương tác",
  lost: "Mất khách",
};

const recordStatusLabelMap = {
  active: "Đang hoạt động",
  closed: "Đã đóng",
  merged: "Đã sáp nhập",
};

const leadSourceLabelMap = {
  Facebook: "Facebook",
  Website: "Website",
  TikTok: "TikTok",
  Referral: "Giới thiệu",
  Event: "Workshop / Sự kiện",
};

const mapOptionLabels = (options = [], labelMap = {}) =>
  (options || []).map((o) => ({
    ...o,
    label: labelMap[o.value] || o.label || o.value,
  }));

const CustomerFilter = ({
  branchOptions = [],
  wardOptions = [],
  statusOptions = [],
  recordStatusOptions = [],
  ownerOptions = [],
  leadSourceOptions = [],
  customerTypeOptions = [],
  schoolLevelOptions = [],
  tagOptions = [],
  onRefresh,
  onNewClick,
  onImportExcelClick,
  onFilterChange,
  onManageTagsClick,
}) => {
  const [advanced, setAdvanced] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef();

  const [filters, setFilters] = useState(() => getDefaultCustomerFilters());

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [debouncedKeyword] = useDebounce(filters.keyword, 400);

  useEffect(() => {
    onFilterChange?.({ ...filters, keyword: debouncedKeyword });
  }, [debouncedKeyword]);

  const resolvedStatusOptions = useMemo(
    () => mapOptionLabels(statusOptions, statusLabelMap),
    [statusOptions]
  );
  const resolvedRecordStatusOptions = useMemo(
    () => mapOptionLabels(recordStatusOptions, recordStatusLabelMap),
    [recordStatusOptions]
  );
  const resolvedLeadSourceOptions = useMemo(
    () => mapOptionLabels(leadSourceOptions, leadSourceLabelMap),
    [leadSourceOptions]
  );

  const updateFilter = (name, value) => {
    let updated = { ...filters, [name]: value };
    if (name === "branch_id") {
      updated = { ...updated, ward: "" };
    }
    if (name === "customer_type" && value !== "Trường học") {
      updated = { ...updated, school_level: "" };
    }
    setFilters(updated);
    if (name !== "keyword") onFilterChange?.(updated);
  };

  const clearFilters = () => {
    const defaults = getDefaultCustomerFilters();
    setFilters(defaults);
    onFilterChange?.(defaults);
  };

  const defaultSnapshot = useMemo(() => getDefaultCustomerFilters(), []);
  const isNonDefault = (key, value) => {
    if (key === "keyword") return Boolean(value);
    return String(value ?? "") !== String(defaultSnapshot[key] ?? "");
  };

  /** Có điều kiện lọc đang áp dụng (kể cả mặc định) */
  const hasActiveFilters = Object.entries(filters).some(
    ([key, value]) => key !== "keyword" && Boolean(value)
  );

  /** Có điều kiện lệch so với mặc định → hiện nút Xóa lọc */
  const isFiltering = Object.entries(filters).some(([key, value]) =>
    isNonDefault(key, value)
  );

  /** Số điều kiện đang áp dụng (kể cả mặc định) — badge trên nút lọc */
  const activeFilterCount = Object.entries(filters).filter(
    ([key, value]) => key !== "keyword" && Boolean(value)
  ).length;

  const handleRefresh = () => {
    onRefresh?.();
    onFilterChange?.(filters);
  };

  const ActionButton = ({
    icon: Icon,
    onClick,
    title,
    active = false,
    badgeCount = 0,
  }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`relative h-11 w-11 flex items-center justify-center rounded-xl border transition-all duration-200 ${
        active
          ? "bg-red-50 border-red-200 text-red-600 shadow-sm"
          : "bg-white border-gray-200 text-gray-500 hover:border-red-400 hover:text-red-500"
      }`}
    >
      <Icon size={18} />
      {badgeCount > 0 ? (
        <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-700 px-1 text-[10px] font-bold text-white">
          {badgeCount}
        </span>
      ) : null}
    </button>
  );

  const selectStyles = {
    ...customSelectStyles,
    control: (provided, state) => ({
      ...(typeof customSelectStyles.control === "function"
        ? customSelectStyles.control(provided, state)
        : provided),
      minHeight: 48,
      borderRadius: 16,
      borderColor: state.isFocused ? "#ef4444" : "#e5e7eb",
      boxShadow: state.isFocused ? "0 0 0 4px rgba(239, 68, 68, 0.05)" : "none",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  };

  const SelectField = ({ label, options, value, onChange, placeholder }) => (
    <div className="flex flex-col gap-1.5 w-full">
      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
        {label}
      </label>
      <Select
        options={options}
        placeholder={placeholder}
        styles={selectStyles}
        value={options.find((x) => x.value === value) || null}
        onChange={(opt) => onChange(opt?.value || "")}
        isClearable
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        menuPosition="fixed"
      />
    </div>
  );

  const advancedFields = (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      <SelectField
        label={TEXT.branch}
        options={branchOptions}
        value={filters.branch_id}
        onChange={(v) => updateFilter("branch_id", v)}
        placeholder={TEXT.allBranch}
      />
      <SelectField
        label={TEXT.ward}
        options={wardOptions}
        value={filters.ward}
        onChange={(v) => updateFilter("ward", v)}
        placeholder={TEXT.allWard}
      />
      <SelectField
        label={TEXT.status}
        options={resolvedStatusOptions}
        value={filters.status}
        onChange={(v) => updateFilter("status", v)}
        placeholder={TEXT.allStatus}
      />
      <SelectField
        label={TEXT.recordStatus}
        options={resolvedRecordStatusOptions}
        value={filters.record_status}
        onChange={(v) => updateFilter("record_status", v)}
        placeholder={TEXT.allRecordStatus}
      />
      <SelectField
        label={TEXT.owner}
        options={ownerOptions}
        value={filters.owner_id}
        onChange={(v) => updateFilter("owner_id", v)}
        placeholder={TEXT.allOwner}
      />
      <SelectField
        label={TEXT.leadSource}
        options={resolvedLeadSourceOptions}
        value={filters.lead_source}
        onChange={(v) => updateFilter("lead_source", v)}
        placeholder={TEXT.allLeadSource}
      />
      <SelectField
        label={TEXT.customerType}
        options={customerTypeOptions}
        value={filters.customer_type}
        onChange={(v) => updateFilter("customer_type", v)}
        placeholder={TEXT.allCustomerType}
      />
      {filters.customer_type === "Trường học" || schoolLevelOptions.length ? (
        <SelectField
          label={TEXT.schoolLevel}
          options={schoolLevelOptions}
          value={filters.school_level}
          onChange={(v) => updateFilter("school_level", v)}
          placeholder={TEXT.allSchoolLevel}
        />
      ) : null}
      <SelectField
        label={TEXT.tag}
        options={tagOptions}
        value={filters.tag_id}
        onChange={(v) => updateFilter("tag_id", v)}
        placeholder={TEXT.allTag}
      />
    </div>
  );

  const chip = (key, options, fieldLabel) => {
    const value = filters[key];
    if (!value) return null;
    const optionLabel =
      options.find((o) => String(o.value) === String(value))?.label || value;
    return (
      <span
        key={key}
        className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-red-100 bg-red-50/80 px-2.5 py-1 text-[11px] font-semibold text-red-700"
        title={`${fieldLabel}: ${optionLabel}`}
      >
        <span className="shrink-0 font-bold uppercase tracking-wide text-red-400">
          {fieldLabel}
        </span>
        <span className="truncate">{optionLabel}</span>
      </span>
    );
  };

  const activeFilterChips = hasActiveFilters ? (
    <div className="flex flex-wrap items-center gap-2">
      {chip("branch_id", branchOptions, TEXT.branch)}
      {chip(
        "record_status",
        resolvedRecordStatusOptions,
        TEXT.recordStatus
      )}
      {chip("ward", wardOptions, TEXT.ward)}
      {chip("status", resolvedStatusOptions, TEXT.status)}
      {chip("owner_id", ownerOptions, TEXT.owner)}
      {chip("lead_source", resolvedLeadSourceOptions, TEXT.leadSource)}
      {chip("customer_type", customerTypeOptions, TEXT.customerType)}
      {chip("school_level", schoolLevelOptions, TEXT.schoolLevel)}
      {chip("tag_id", tagOptions, TEXT.tag)}
      {isFiltering ? (
        <button
          type="button"
          onClick={clearFilters}
          className="rounded-full px-2.5 py-1 text-[11px] font-bold text-red-600 hover:bg-red-50"
        >
          {TEXT.clear}
        </button>
      ) : null}
    </div>
  ) : null;

  const handleOpenManual = async () => {
    try {
      const guideUrl = `${window.location.origin}/guides/customer-management-guide.html`;
      window.open(guideUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error(err);
      alert(TEXT.guideLoadFailed);
    }
    setShowMenu(false);
  };

  if (isMobile) {
    return (
      <div className="mb-6 space-y-4">
        <div className="flex gap-2">
          <div className="relative flex-1 group">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
              size={18}
            />
            <input
              type="text"
              placeholder={TEXT.searchPlaceholder}
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full bg-white border border-gray-200 pl-11 pr-4 py-3 rounded-2xl focus:outline-none focus:border-red-500 shadow-sm transition-all"
            />
          </div>
          <ActionButton
            icon={RotateCw}
            onClick={handleRefresh}
            title={TEXT.refresh}
          />
          <button
            type="button"
            onClick={() => setAdvanced(true)}
            title={TEXT.advanced}
            className={`relative h-11 w-11 flex items-center justify-center rounded-xl border transition-all duration-200 ${
              activeFilterCount > 0 || advanced
                ? "bg-red-50 border-red-200 text-red-600 shadow-sm"
                : "bg-white border-gray-200 text-gray-500 hover:border-red-400 hover:text-red-500"
            }`}
          >
            <SlidersHorizontal size={18} />
            {activeFilterCount > 0 ? (
              <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-700 px-1 text-[10px] font-bold text-white">
                {activeFilterCount}
              </span>
            ) : null}
          </button>
        </div>

        {/* Nhập excel chỉ hỗ trợ desktop — mobile chỉ giữ nút thêm mới */}
        {hasPermission("crm.customers", "C") ? (
          <div className="pointer-events-none fixed bottom-24 right-4 z-40 flex flex-col items-end gap-3">
            <button
              type="button"
              onClick={onNewClick}
              className="pointer-events-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-700 text-white shadow-lg shadow-red-200 active:scale-95"
              title={TEXT.addCustomer}
            >
              <Plus size={24} strokeWidth={2.5} />
            </button>
          </div>
        ) : null}

        {!advanced && activeFilterChips ? (
          <div className="pt-1">{activeFilterChips}</div>
        ) : null}

        <div className="flex gap-2 overflow-x-auto pb-1">
          {onManageTagsClick ? (
            <button
              type="button"
              onClick={onManageTagsClick}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-gray-600"
            >
              <Tags size={14} />
              Tags
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleOpenManual}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-gray-600"
          >
            <BookOpen size={14} />
            Hướng dẫn
          </button>
        </div>
        <MobileAdvancedFilterSheet
          open={advanced}
          onClose={() => setAdvanced(false)}
          title={TEXT.advancedTitle}
          footer={
            <>
              {isFiltering ? (
                <button
                  type="button"
                  onClick={() => {
                    clearFilters();
                    setAdvanced(false);
                  }}
                  className="flex-1 py-4 border border-gray-300 text-gray-600 rounded-2xl font-bold hover:bg-gray-50"
                >
                  {TEXT.clear}
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setAdvanced(false)}
                className="flex-1 py-4 bg-red-600 text-white rounded-2xl font-bold shadow-lg shadow-red-200"
              >
                {TEXT.apply}
              </button>
            </>
          }
        >
          {advancedFields}
        </MobileAdvancedFilterSheet>
      </div>
    );
  }

  return (
    <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm mb-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative w-full max-w-sm group">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
              size={18}
            />
            <input
              type="text"
              placeholder={TEXT.searchPlaceholder}
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full bg-gray-50 border-transparent border-2 focus:bg-white focus:border-red-500 pl-11 pr-4 py-2.5 rounded-2xl outline-none transition-all font-medium text-gray-700"
            />
          </div>

          <ActionButton
            icon={RotateCw}
            onClick={handleRefresh}
            title={TEXT.refresh}
          />
          <ActionButton
            icon={SlidersHorizontal}
            onClick={() => setAdvanced(!advanced)}
            active={advanced || activeFilterCount > 0}
            title={TEXT.advanced}
            badgeCount={advanced ? 0 : activeFilterCount}
          />

          {isFiltering && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-xs font-bold text-red-500 hover:text-red-700 flex items-center gap-1 uppercase tracking-wider transition-all ml-2"
            >
              <Eraser size={14} /> {TEXT.clear}
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          {hasPermission("crm.customers", "I") && (
            <button
              type="button"
              onClick={onImportExcelClick}
              className="flex items-center gap-2 px-5 py-2.5 bg-white text-red-700 rounded-full font-bold border border-red-100 hover:shadow-lg hover:shadow-red-50 transition-all active:scale-95 uppercase text-xs tracking-wider"
            >
              <UploadCloud size={18} />
              {TEXT.importExcel}
            </button>
          )}
          {hasPermission("crm.customers", "C") && (
            <button
              type="button"
              onClick={onNewClick}
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-red-700 to-red-800 text-white rounded-full font-bold hover:shadow-lg hover:shadow-red-100 transition-all active:scale-95 uppercase text-xs tracking-wider"
            >
              <Plus size={18} />
              {TEXT.addCustomer}
            </button>
          )}

          <div className="relative">
            <button
              type="button"
              onClick={() => setShowMenu(!showMenu)}
              title={TEXT.moreOptions}
              className={`h-11 w-11 flex items-center justify-center rounded-xl border transition-all ${
                showMenu
                  ? "bg-gray-100 border-gray-300"
                  : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
              }`}
            >
              <MoreVertical size={18} />
            </button>

            <AnimatePresence>
              {showMenu && (
                <motion.div
                  ref={menuRef}
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  className="absolute right-0 mt-3 w-56 bg-white border border-gray-100 rounded-[1.5rem] shadow-2xl z-50 p-2 overflow-hidden"
                  onMouseLeave={() => setShowMenu(false)}
                >
                  {hasPermission("crm.customers", "C") && (
                    <button
                      type="button"
                      onClick={() => {
                        onManageTagsClick?.();
                        setShowMenu(false);
                      }}
                      className="flex items-center gap-3 w-full px-4 py-3 text-xs font-bold text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all"
                    >
                      <Tags size={16} />
                      {TEXT.manageTags}
                    </button>
                  )}

                  {hasPermission("crm.customers", "R") && (
                    <button
                      type="button"
                      onClick={handleOpenManual}
                      className="flex items-center gap-3 w-full px-4 py-3 text-xs font-bold text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all"
                    >
                      <BookOpen size={16} />
                      {TEXT.guideHtml}
                    </button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {!advanced && activeFilterChips ? (
        <div className="mt-4 border-t border-gray-50 pt-4">{activeFilterChips}</div>
      ) : null}

      <AnimatePresence>
        {advanced && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-6 border-t border-gray-100 pt-6">{advancedFields}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CustomerFilter;
